<template>
  <pn-toast
    v-if="error"
    temporary
    :text="error"
    appearance="error"
    class="notification"
    @close="priceManagementStore.clearError()"
  ></pn-toast>
  <pn-toast
    v-if="actionMessage.show"
    temporary
    :text="actionMessage.text"
    :appearance="actionMessage.success ? 'success' : 'error'"
    class="notification"
    @close="actionMessage.show = false"
  ></pn-toast>
  <div :class="[{ spinner: isLoading }]">
    <pn-spinner size="4" v-show="isLoading" />
  </div>
  <h1>Mail Out Price Management</h1>
  <section>
    <pn-table>
      <table class="price-table">
        <colgroup>
          <col class="col-name" />
          <col class="col-currency" />
          <col class="col-date" />
          <col class="col-date" />
          <col class="col-status" />
          <col class="col-action" />
          <col class="col-toggle" />
        </colgroup>
        <thead>
          <tr>
            <th>Name</th>
            <th>Currency Code</th>
            <th>Effective From</th>
            <th>Effective To</th>
            <th>Status</th>
            <th>Action</th>
            <th class="toggle-header"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="priceLists.length === 0 && !isLoading">
            <td colspan="7" class="text-center">No records found</td>
          </tr>
          <template v-if="priceLists.length > 0">
            <template v-for="priceList in priceLists" :key="priceList.id">
              <tr
                class="accordion-label-row"
                :class="{
                  'menu-open-row': openActionMenuRowId === priceList.id,
                }"
              >
                <td>{{ priceList?.priceList?.priceListName }}</td>
                <td>{{ priceList?.priceList?.currencyCode }}</td>
                <td>{{ utc2local(priceList?.priceList?.effectiveFrom) }}</td>
                <td>{{ utc2local(priceList?.priceList?.effectiveTo) }}</td>
                <td>{{ priceList?.priceList?.status }}</td>
                <td class="action-cell">
                  <div class="action-buttons">
                    <pn-button
                      :icon="edit"
                      appearance="light"
                      variant="borderless"
                      icon-only="true"
                      arialabel="Edit"
                      title="Edit"
                      @click="openEditPriceListModal(priceList.id)"
                    ></pn-button>
                    <pn-button
                      :icon="layers"
                      appearance="light"
                      variant="borderless"
                      icon-only="true"
                      arialabel="Clone"
                      title="Clone"
                      @click="cloneList(priceList.priceListId)"
                    ></pn-button>
                    <pn-button
                      :icon="trash_can"
                      appearance="light"
                      variant="borderless"
                      icon-only="true"
                      arialabel="Delete"
                      title="Delete"
                      @click="
                        openDeleteConfirmationModal(priceList.priceListId)
                      "
                    ></pn-button>
                  </div>
                </td>
                <td class="toggle-cell">
                  <div
                    class="row-actions"
                    :class="{
                      'menu-open': openActionMenuRowId === priceList.id,
                    }"
                  >
                    <pn-button
                      :icon="angle_down"
                      appearance="light"
                      variant="borderless"
                      class="expand-button"
                      :class="{ 'is-open': isPriceListExpanded(priceList.id) }"
                      @click.stop="togglePriceList(priceList.id)"
                    ></pn-button>
                  </div>
                </td>
              </tr>
              <tr
                v-if="isPriceListExpanded(priceList.id)"
                class="accordion-row"
              >
                <td colspan="7" class="accordion-cell">
                  <div class="accordion-content">
                    <pn-table bordered="true">
                      <table class="vat-price-table">
                        <colgroup>
                          <col class="vat-col-code" />
                          <col class="width-12" />
                          <col class="vat-col-amount" />
                          <col class="vat-col-calculation-method" />
                          <col class="vat-col-effective-from" />
                          <col class="vat-col-effective-to" />
                          <col class="vat-col-action" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>VAT CODE</th>
                            <th>Rate</th>
                            <th>Amount</th>
                            <th>Calculation Method</th>
                            <th>Effective From</th>
                            <th>Effective To</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="vatChargePrice in priceList.vatChargePrice"
                            :key="vatChargePrice.id"
                          >
                            <td>{{ vatChargePrice?.vatCode }}</td>
                            <td>{{ vatChargePrice?.vatRate }}</td>
                            <td>{{ vatChargePrice?.vatAmount }}</td>
                            <td>{{ vatChargePrice?.calculationMethod }}</td>
                            <td>
                              {{ utc2local(vatChargePrice?.effectiveFrom) }}
                            </td>
                            <td>
                              {{ utc2local(vatChargePrice?.effectiveTo) }}
                            </td>
                            <td class="action-cell">
                              <pn-button
                                :icon="edit"
                                appearance="light"
                                variant="borderless"
                                icon-only="true"
                                arialabel="Edit"
                                title="Edit"
                                @click="
                                  openEditVatPriceModal(
                                    priceList.priceList.id,
                                    vatChargePrice.id,
                                  )
                                "
                              ></pn-button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </pn-table>
                    <pn-table bordered="true">
                      <table class="sap-details-table">
                        <colgroup>
                          <col class="sap-col-name" />
                          <col class="sap-col-price" />
                          <col class="sap-col-type" />
                          <col class="sap-col-min" />
                          <col class="sap-col-max" />
                          <col class="sap-col-effective-from" />
                          <col class="sap-col-effective-to" />
                          <col class="sap-col-status" />
                          <col class="sap-col-action" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th class="sap-product-name-column">
                              SAP Product Name
                            </th>
                            <th>Price</th>
                            <th>Type</th>
                            <th>Min Qty</th>
                            <th>Max Qty</th>
                            <th>Effective From</th>
                            <th>Effective To</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="sapPriceDetail in priceList.sapPriceDetails"
                            :key="sapPriceDetail.id"
                          >
                            <td class="sap-product-name-cell">
                              {{
                                sapPriceDetail?.sapProductMap?.sapProduct
                                  ?.sapProductName
                              }}<br />
                              ({{
                                getProductName(
                                  sapPriceDetail?.sapProductMap?.sapProduct
                                    ?.sapProductName,
                                )
                              }})
                            </td>
                            <td>{{ sapPriceDetail?.price }}</td>
                            <td>{{ sapPriceDetail?.priceType }}</td>
                            <td>{{ sapPriceDetail?.minQty }}</td>
                            <td>{{ sapPriceDetail?.maxQty }}</td>
                            <td>
                              {{ utc2local(sapPriceDetail?.effectiveFrom) }}
                            </td>
                            <td>
                              {{ utc2local(sapPriceDetail?.effectiveTo) }}
                            </td>
                            <td>{{ sapPriceDetail?.status }}</td>
                            <td class="action-cell">
                              <pn-button
                                :icon="edit"
                                appearance="light"
                                variant="borderless"
                                icon-only="true"
                                arialabel="Edit"
                                title="Edit"
                                @click="
                                  openEditSapPriceModal(
                                    priceList.priceList.id,
                                    sapPriceDetail.id,
                                  )
                                "
                              ></pn-button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </pn-table>
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </pn-table>
  </section>
  <pn-modal
    :open="isEditPriceListModalOpen"
    sheet="true"
    max-width="500px"
    @close="closeEditPriceListModal"
  >
    <component
      :is="activeModalComponent"
      v-if="activeModalComponent && selectedPriceListId"
      :price-list-id="selectedPriceListId"
      @close="closeEditPriceListModal"
    ></component>
  </pn-modal>
  <ConfirmationModal
    :open="showDeleteConfirmationModal"
    :showLoader="isLoading"
    heading="Are you sure you want to delete this price list?"
    @onCancel="closeDeleteConfirmationModal"
    @onConfirm="confirmDeletePriceList"
  />
  <EditVatPriceModal
    :open="isEditVatPriceModalOpen"
    :price-list-id="selectedVatPriceListId"
    :vat-charge-price-id="selectedVatChargePriceId"
    @onClose="closeEditVatPriceModal"
  />
  <EditSapPriceModal
    :open="isEditSapPriceModalOpen"
    :price-list-id="selectedSapPriceListId"
    :sap-price-detail-id="selectedSapPriceDetailId"
    @onClose="closeEditSapPriceModal"
  />
</template>

<script setup lang="ts">
import { onMounted, reactive, ref, shallowRef } from "vue";
import { storeToRefs } from "pinia";
import { usePriceManagementStore } from "@/stores/priceManagement";
import { utc2local } from "@/utils";
import constant from "@/constant";
import {
  angle_down,
  layers,
  edit,
  trash_can,
} from "pn-design-assets/pn-assets/icons.js";
import EditPriceList from "@/components/order/modals/EditPriceList.vue";
import ConfirmationModal from "./modals/ConfirmationModal.vue";
import EditVatPriceModal from "./modals/EditVatPriceModal.vue";
import EditSapPriceModal from "./modals/EditSapPriceModal.vue";

const { PRICE_MANAGEMENT } = constant;
const priceManagementStore = usePriceManagementStore();
const { priceLists, isLoading, error } = storeToRefs(priceManagementStore);
const productNameDictionary: Record<string, string> = {
  W2P_BASE_ADR_STARTAVGIFT: PRICE_MANAGEMENT.W2P_BASE_ADR_STARTAVGIFT,
  W2P_ADR_DISTRIBUTION: PRICE_MANAGEMENT.W2P_ADR_DISTRIBUTION,
  W2P_PRINT_PRICE_POSTCARDS: PRICE_MANAGEMENT.W2P_PRINT_PRICE_POSTCARDS,
  W2P_REFERENCE_SAMPLES: PRICE_MANAGEMENT.W2P_REFERENCE_SAMPLES,
  W2P_ADDRESS_PURCHASE: PRICE_MANAGEMENT.W2P_ADDRESS_PURCHASE,
};
const expandedPriceListIds = reactive<Record<string, boolean>>({});
const openActionMenuRowId = ref<string | null>(null);
const isEditPriceListModalOpen = ref(false);
const selectedPriceListId = ref<string | null>(null);
const activeModalComponent = shallowRef<typeof EditPriceList | null>(null);
const showDeleteConfirmationModal = ref(false);
const selectedDeletePriceListId = ref<string | null>(null);
const isEditVatPriceModalOpen = ref(false);
const selectedVatPriceListId = ref<string | null>(null);
const selectedVatChargePriceId = ref<string | null>(null);
const isEditSapPriceModalOpen = ref(false);
const selectedSapPriceListId = ref<string | null>(null);
const selectedSapPriceDetailId = ref<string | null>(null);
const actionMessage = ref({
  show: false,
  text: "",
  success: true,
});
const getProductName = (name?: string): string => {
  if (!name) {
    return "NA";
  }
  return productNameDictionary[name] ?? "NA";
};
const isPriceListExpanded = (priceListId: string): boolean => {
  return !!expandedPriceListIds[priceListId];
};
const togglePriceList = (priceListId: string) => {
  expandedPriceListIds[priceListId] = !expandedPriceListIds[priceListId];
};
const openEditPriceListModal = (priceListId: string) => {
  selectedPriceListId.value = priceListId;
  activeModalComponent.value = EditPriceList;
  isEditPriceListModalOpen.value = true;
};
const cloneList = async (priceListId: string) => {
  const result = await priceManagementStore.clonePriceList(priceListId);
  if (result.status === "error") {
    showActionMessage(result.message, false);
    return;
  }
  showActionMessage(PRICE_MANAGEMENT.CLONE_SUCCESS_MESSAGE, true);
};
const openDeleteConfirmationModal = (priceListId: string) => {
  selectedDeletePriceListId.value = priceListId;
  showDeleteConfirmationModal.value = true;
};
const closeDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = false;
  selectedDeletePriceListId.value = null;
};
const confirmDeletePriceList = async () => {
  if (!selectedDeletePriceListId.value) {
    closeDeleteConfirmationModal();
    return;
  }
  const result = await priceManagementStore.deletePriceList(
    selectedDeletePriceListId.value,
  );
  closeDeleteConfirmationModal();
  if (result.status === "error") {
    showActionMessage(result.message, false);
    return;
  }
  showActionMessage(PRICE_MANAGEMENT.DELETE_SUCCESS_MESSAGE, true);
};
const showActionMessage = (text: string, success: boolean) => {
  actionMessage.value = {
    show: true,
    text,
    success,
  };
  setTimeout(() => {
    actionMessage.value.show = false;
  }, 4000);
};
const closeEditPriceListModal = (result?: {
  status: "success";
  message: string;
}) => {
  isEditPriceListModalOpen.value = false;
  activeModalComponent.value = null;
  selectedPriceListId.value = null;
  if (result?.status === "success") {
    showActionMessage(result.message, true);
  }
};
const openEditVatPriceModal = (
  priceListId: string,
  vatChargePriceId: string,
) => {
  selectedVatPriceListId.value = priceListId;
  selectedVatChargePriceId.value = vatChargePriceId;
  isEditVatPriceModalOpen.value = true;
};
const closeEditVatPriceModal = (result?: {
  status: "success";
  message: string;
}) => {
  isEditVatPriceModalOpen.value = false;
  selectedVatPriceListId.value = null;
  selectedVatChargePriceId.value = null;
  if (result?.status === "success") {
    showActionMessage(result.message, true);
  }
};
const openEditSapPriceModal = (
  priceListId: string,
  sapPriceDetailId: string,
) => {
  selectedSapPriceListId.value = priceListId;
  selectedSapPriceDetailId.value = sapPriceDetailId;
  isEditSapPriceModalOpen.value = true;
};
const closeEditSapPriceModal = (result?: {
  status: "success";
  message: string;
}) => {
  isEditSapPriceModalOpen.value = false;
  selectedSapPriceListId.value = null;
  selectedSapPriceDetailId.value = null;
  if (result?.status === "success") {
    showActionMessage(result.message, true);
  }
};

onMounted(() => {
  priceManagementStore.getPriceLists();
});
</script>

<style lang="scss" scoped>
.accordion-row td {
  padding: 0;
  overflow: visible;
}

.accordion-label-row td {
  border-bottom: 1px solid #d3cecb;
}

.accordion-label-row {
  position: relative;
  z-index: 1;
}

.accordion-label-row.menu-open-row {
  z-index: 60;
}
.accordion-cell {
  border: 0;
  overflow: visible;
}

:deep(pn-table) {
  overflow: visible;
}

.toggle-cell {
  min-width: 84px;
  padding-left: 8px;
}

.action-cell {
  min-width: 132px;
  padding-right: 8px;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  white-space: nowrap;
}

.row-actions {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
}

.row-actions.menu-open {
  z-index: 70;
}

.expand-button {
  transform: rotate(0deg);
  transition: transform 0.2s ease-in-out;
}

.expand-button.is-open {
  transform: rotate(180deg);
}

.action-menu-cell {
  display: flex;
  align-items: center;
  overflow: visible;
  position: relative;
  z-index: 1;
}

:deep(pn-action-menu.price-action-menu) {
  position: relative;
  z-index: 20;
}
.accordion-content {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
}
.accordion-content table {
  width: 100%;
}
.vat-price-table {
  width: 100%;
  table-layout: fixed;
}
.vat-col-code {
  width: 18%;
}
.vat-col-amount {
  width: 12%;
}
.vat-col-calculation-method {
  width: 24%;
}
.vat-col-effective-from,
.vat-col-effective-to {
  width: 17%;
}
.vat-col-action {
  width: 12%;
}
.vat-price-table th,
.vat-price-table td,
.sap-details-table th,
.sap-details-table td {
  vertical-align: top;
  text-align: center;
  padding: 12px;
}
.vat-price-table th:nth-child(2),
.vat-price-table td:nth-child(2),
.vat-price-table th:nth-child(4),
.vat-price-table td:nth-child(4),
.sap-details-table th:nth-child(2),
.sap-details-table td:nth-child(2),
.sap-details-table th:nth-child(3),
.sap-details-table td:nth-child(3),
.sap-details-table th:nth-child(4),
.sap-details-table td:nth-child(4),
.sap-details-table th:nth-child(5),
.sap-details-table td:nth-child(5),
.sap-details-table th:nth-child(8),
.sap-details-table td:nth-child(8) {
  white-space: nowrap;
}
.sap-details-table {
  width: 100%;
  table-layout: fixed;
}
.sap-col-name {
  width: 31%;
}
.sap-col-price,
.sap-col-type,
.sap-col-min,
.sap-col-max {
  width: 7%;
}
.sap-col-effective-from,
.sap-col-effective-to {
  width: 12%;
}
.sap-col-status {
  width: 7%;
}
.sap-col-action {
  width: 10%;
}
.sap-product-name-column,
.sap-product-name-cell {
  white-space: normal;
}
.sap-product-name-cell {
  overflow-wrap: anywhere;
  word-break: break-word;
}
.price-table {
  width: 100%;
  table-layout: fixed;
}
.price-table .col-name {
  width: 21%;
}
.price-table .col-currency {
  width: 13%;
}
.price-table .col-date {
  width: 17%;
}
.price-table .col-status {
  width: 11%;
}

.price-table .col-action {
  width: 12%;
}

.price-table .col-toggle {
  width: 9%;
}

.toggle-header {
  padding: 0;
}
.notification {
  position: fixed;
  bottom: 12px;
  padding: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}
.spinner {
  width: 100%;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
